<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <oxd-table-filter
      :filter-title="$t('leave.my_leave_entitlement_and_usage_report')"
    >
      <oxd-form @submit-valid="generateReport">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <leave-period-dropdown
                v-model="filters.leavePeriod"
                :rules="rules.leavePeriod"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <oxd-button
            type="submit"
            display-type="secondary"
            class="orangehrm-left-space"
            :label="$t('general.generate')"
          />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>

    <br />

    <div v-if="showTable" class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <oxd-text tag="span">
          {{ $t('general.n_records_found', {count: items.length}) }}
        </oxd-text>
      </div>
      <table-header :total="items.length" :loading="isLoading" />
      <div class="orangehrm-container">
        <oxd-card-table
          :headers="tableHeaders"
          :items="items"
          :clickable="false"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {onBeforeMount, ref} from 'vue';
import {required} from '@/core/util/validation/rules';
import {APIService} from '@/core/util/services/api.service';
import LeavePeriodDropdown from '@/orangehrmLeavePlugin/components/LeavePeriodDropdown';

export default {
  name: 'MyLeaveEntitlementReport',
  components: {
    'leave-period-dropdown': LeavePeriodDropdown,
  },
  props: {
    leavePeriod: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/leave/reports/data',
    );
    const filters = ref({
      leavePeriod: props.leavePeriod ? props.leavePeriod : null,
    });
    const rules = ref({
      leavePeriod: [required],
    });
    const isLoading = ref(false);
    const showTable = ref(false);
    const items = ref([]);

    const tableHeaders = [
      {
        name: 'leaveTypeName',
        title: 'Leave Type',
        style: {flex: 1.4},
      },
      {
        name: 'entitlementDays',
        title: 'Entitlements (Days)',
        style: {flex: 1},
      },
      {
        name: 'pendingApprovalDays',
        title: 'Pending Approval (Days)',
        style: {flex: 1},
      },
      {
        name: 'scheduledDays',
        title: 'Scheduled (Days)',
        style: {flex: 1},
      },
      {
        name: 'takenDays',
        title: 'Taken (Days)',
        style: {flex: 1},
      },
      {
        name: 'balanceDays',
        title: 'Balance (Days)',
        style: {flex: 1},
      },
    ];

    const generateReport = async () => {
      isLoading.value = true;
      showTable.value = true;
      try {
        const response = await http.getAll({
          name: 'my_leave_entitlements_and_usage',
          fromDate: filters.value.leavePeriod?.startDate,
          toDate: filters.value.leavePeriod?.endDate,
          _dateFormattingEnabled: true,
          limit: 50,
          offset: 0,
        });
        const {data} = response.data;
        items.value = Array.isArray(data) ? data : [];
      } catch (e) {
        items.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    onBeforeMount(() => {
      if (filters.value.leavePeriod?.startDate) {
        generateReport();
      }
    });

    return {
      rules,
      filters,
      items,
      isLoading,
      showTable,
      tableHeaders,
      generateReport,
    };
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-header-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 1.2rem 25px 0;
}
</style>
